/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './StatementsTable.scss';

import { isEmpty, kebabCase } from 'lodash';
import { arrayOf, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as ViewIcon } from 'assets/icons/link-external.svg';
import { DATE_DISPLAY_FORMAT, YEAR_DISPLAY_FORMAT } from 'services/Dates';
import { getStatementsFilteringSelections } from 'state/statements/StatementsSelectors';
import { getCurrentOwnedAccounts } from 'state/account/AccountSelectors';
import { getDocumentAndOpen, getDocumentAndDownload } from 'services/Rest';
import { statementsNotAvailableText } from 'services/Statements';
import { FormattedDate } from 'components/shared/Text/Text';
import Optioner from 'components/shared/Optioner/Optioner';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import BaseModal from 'components/shared/Modal/BaseModal';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import Content from 'services/Content';

const TAX_FORM = 'Tax Form';
const DATE = 'Date';
const TYPE = 'Type';
const NAME = 'Name';
const VIEW = 'View';
const DOWNLOAD = 'Download';
export const columns = [DATE, TYPE, NAME, VIEW, DOWNLOAD];

const Date = ({ date, type }) => (
  <FormattedDate
    element="td"
    format={type === TAX_FORM ? YEAR_DISPLAY_FORMAT : DATE_DISPLAY_FORMAT}
    className="statements-table-date-cell">
    {date}
  </FormattedDate>
);

Date.propTypes = {
  date: string.isRequired,
  type: string
};

const Type = ({ type }) => (
  <td className="statements-table-type-cell">{type}</td>
);

Type.propTypes = {
  type: string.isRequired
};

const Name = ({ name }) => (
  <td className="statements-table-name-cell blocked_data">{name}</td>
);

Name.propTypes = {
  name: string.isRequired
};

const PdfViewButton = ({
  accounts,
  accountNumber,
  accountChecksum,
  name,
  documentId,
  productType,
  typeId,
  requestStatus
}) => {
  const [showSpinner, setShowSpinner] = useState(false);
  let receivedDocument =
    requestStatus[`documentDownload_${documentId}`] === REQUEST_STATES.received;

  useEffect(() => {
    if (receivedDocument && showSpinner) {
      setTimeout(() => {
        setShowSpinner(false);
      }, 500);
    }
  }, [receivedDocument, showSpinner]);

  const handleView = () => {
    setShowSpinner(true);
    getDocumentAndOpen(
      accounts,
      accountNumber,
      accountChecksum,
      documentId,
      name,
      productType,
      typeId
    );
  };

  return (
    <td className="statements-table-pdf-cell">
      {showSpinner ? (
        <span className="statements-spinner">
          <Spinner />
        </span>
      ) : (
        <a id="view-pdf">
          <ViewIcon
            className="pdf-function view"
            onClick={() => handleView()}
            requestStatusKey={`documentDownload_${documentId}`}>
            View
          </ViewIcon>
        </a>
      )}
    </td>
  );
};

const PdfDownloadButton = ({
  accounts,
  accountNumber,
  accountChecksum,
  name,
  documentId,
  productType,
  typeId,
  requestStatus
}) => {
  const [showSpinner, setShowSpinner] = useState(false);
  let receivedDocument =
    requestStatus[`documentDownload_${documentId}`] === REQUEST_STATES.received;

  useEffect(() => {
    if (receivedDocument && showSpinner) {
      setTimeout(() => {
        setShowSpinner(false);
      }, 500);
    }
  }, [receivedDocument, showSpinner]);

  const handleDownload = () => {
    setShowSpinner(true);
    getDocumentAndDownload(
      accounts,
      accountNumber,
      accountChecksum,
      documentId,
      name,
      productType,
      typeId
    );
  };

  return (
    <td className="statements-table-pdf-cell">
      {showSpinner ? (
        <span className="statements-spinner">
          <Spinner />
        </span>
      ) : (
        <a id="download-pdf">
          <DownloadIcon
            className="pdf-function download"
            onClick={() => handleDownload()}
            requestStatusKey={`documentDownload_${documentId}`}>
            Download
          </DownloadIcon>
        </a>
      )}
    </td>
  );
};

export const StatementTableHeader = () => (
  <tr className="statements-table-columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={`statements-table-header-cell statements-table-header-${kebabCase(
          name.toLowerCase()
        )}`}
        key={name}>
        {name ? name : ''}
        {name === VIEW && (
          <BaseModal
            containerClassName="view-statements-disclosure-container"
            title="Important Information">
            <Disclosure content={Content.viewStatementsTooltipCopy} />
          </BaseModal>
        )}
      </th>
    ))}
  </tr>
);

const StatementTableRows = ({ accounts, rows, requestStatus }) =>
  rows.map((data, key) => (
    <tr className="statements-table-row" key={key}>
      <StatementTableCells
        accounts={accounts}
        data={data}
        requestStatus={requestStatus}
      />
    </tr>
  ));

StatementTableRows.propTypes = {
  rows: arrayOf(object).isRequired
};

const StatementTableCells = ({
  accounts,
  requestStatus,
  data: {
    accountNumber,
    accountChecksum,
    date,
    documentId,
    name,
    productType,
    type,
    typeId
  } = {}
}) =>
  columns.map((column, key) => (
    <Optioner key={key} val={column}>
      {{
        [DATE]: <Date type={type} date={date} />,
        [TYPE]: <Type type={type} />,
        [NAME]: <Name name={name} />,
        [VIEW]: (
          <PdfViewButton
            accounts={accounts}
            accountNumber={accountNumber}
            accountChecksum={accountChecksum}
            documentId={documentId}
            name={name}
            productType={productType}
            typeId={typeId}
            requestStatus={requestStatus}
          />
        ),
        [DOWNLOAD]: (
          <PdfDownloadButton
            accounts={accounts}
            accountNumber={accountNumber}
            accountChecksum={accountChecksum}
            documentId={documentId}
            name={name}
            productType={productType}
            typeId={typeId}
            requestStatus={requestStatus}
          />
        )
      }}
    </Optioner>
  ));

StatementTableCells.propTypes = {
  data: object.isRequired
};

const StatementsTable = ({
  accounts,
  rows,
  statementsFilteringSelections,
  hasAlaska529Only,
  requestStatus
}) => {
  const { documentTypes, timePeriod, productTypes } =
    statementsFilteringSelections;
  const statementUnavailableText = statementsNotAvailableText(
    documentTypes,
    timePeriod,
    productTypes,
    hasAlaska529Only
  );

  return (
    <div className="statements-table-container module module-container">
      {isEmpty(rows) ? (
        <div className="statements-table-no-statements">
          {statementUnavailableText}
        </div>
      ) : (
        <table className="statements-table">
          <thead className="statements-table-header">
            <StatementTableHeader />
          </thead>
          <tbody>
            <StatementTableRows
              accounts={accounts}
              rows={rows}
              requestStatus={requestStatus}
            />
          </tbody>
        </table>
      )}
    </div>
  );
};

StatementsTable.propTypes = {
  rows: arrayOf(object).isRequired
};

const mapStateToProps = (state) => ({
  accounts: getCurrentOwnedAccounts(state),
  statementsFilteringSelections: getStatementsFilteringSelections(state),
  hasAlaska529Only: state.view.hasAlaska529Only,
  requestStatus: getRequestStatus(state)
});

export default connect(mapStateToProps)(StatementsTable);
